/**
 * L7 Platform — progressive quote-carousel enhancement for Customer Voices.
 *
 * Transforms `.l7-voices` / `.l7-voices-section .l7-voices` cards grids into an
 * auto-rotating quote carousel with pill indicators. Without JS the cards
 * render stacked as authored — no content loss. Never mutates the Visual
 * Builder DOM (`body.et-fb`). Sidebar cards outside `.l7-voices` are untouched.
 */
(function (window, document) {
	'use strict';

	// `module_class="l7-voices"` lands on the Divi outer module wrapper
	// (`.et_pb_module.l7_cards.l7-voices`); the inner render root is `.l7-cards`.
	var SELECTOR = '.l7-voices, .l7-voices-section .l7-voices';
	var INTERVAL_MS = 6000;
	var INTERVAL_MIN_MS = 2000;
	var INTERVAL_MAX_MS = 20000;

	/**
	 * @return {boolean}
	 */
	function isVisualBuilder() {
		return document.body && document.body.classList.contains('et-fb');
	}

	/**
	 * @return {boolean}
	 */
	function prefersReducedMotion() {
		return (
			window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		);
	}

	/**
	 * @param {number} index Carousel index on the page.
	 * @return {string}
	 */
	function uid(index) {
		return 'l7-voices-' + index;
	}

	/**
	 * Resolve the inner `.l7-cards` grid (or the node itself when it is one).
	 *
	 * @param {Element} root Opt-in wrapper or cards root.
	 * @return {Element}
	 */
	function resolveCardsRoot(root) {
		if (root.classList.contains('l7-cards')) {
			return root;
		}
		return root.querySelector('.l7-cards') || root;
	}

	/**
	 * Collect Divi card child wrappers for a voices cards root.
	 *
	 * @param {Element} cards `.l7-cards` root.
	 * @return {Element[]}
	 */
	function collectSlides(cards) {
		var wrappers = cards.querySelectorAll(
			':scope > .et_pb_module.l7_card, :scope > .l7_card, :scope > .l7-card'
		);

		if (wrappers.length) {
			return Array.prototype.slice.call(wrappers);
		}

		return Array.prototype.slice.call(cards.querySelectorAll('.l7-card'));
	}

	/**
	 * Label for a slide — title text, else "Quote N".
	 *
	 * @param {Element} wrapper Slide wrapper.
	 * @param {number} index Zero-based index.
	 * @return {string}
	 */
	function slideLabel(wrapper, index) {
		var title = wrapper.querySelector('.l7-card__title');
		if (title) {
			var text = title.textContent.replace(/\s+/g, ' ').trim();
			if (text) {
				return text;
			}
		}
		return 'Quote ' + (index + 1);
	}

	/**
	 * Build initials from a name string.
	 * Strips common honorific prefixes (Dr., Mr., Mrs., Ms., Prof.) first.
	 *
	 * @param {string} name Person name.
	 * @return {string}
	 */
	function initialsFrom(name) {
		var cleaned = name
			.replace(/\s+/g, ' ')
			.trim()
			.replace(/^(Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, '');
		var parts = cleaned.split(' ');
		if (!parts.length || !parts[0]) {
			return '';
		}
		if (parts.length === 1) {
			return parts[0].charAt(0).toUpperCase();
		}
		return (
			parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
		).toUpperCase();
	}

	/**
	 * Ensure each slide has a decorative initials avatar when a title exists.
	 *
	 * @param {Element} wrapper Slide wrapper.
	 * @return {void}
	 */
	function ensureAvatar(wrapper) {
		if (wrapper.querySelector('.l7-voices__avatar')) {
			return;
		}
		var title = wrapper.querySelector('.l7-card__title');
		if (!title) {
			return;
		}
		var initials = initialsFrom(title.textContent);
		if (!initials) {
			return;
		}
		var avatar = document.createElement('span');
		avatar.className = 'l7-voices__avatar';
		avatar.setAttribute('aria-hidden', 'true');
		avatar.textContent = initials;

		var body = wrapper.querySelector('.l7-card__body') || wrapper;
		var attr = document.createElement('div');
		attr.className = 'l7-voices__attribution';
		attr.appendChild(avatar);

		// Move title + eyebrow into the attribution cluster.
		var eyebrow = body.querySelector('.l7-card__eyebrow');
		var meta = document.createElement('div');
		meta.className = 'l7-voices__meta';
		meta.appendChild(title);
		if (eyebrow) {
			meta.appendChild(eyebrow);
		}
		attr.appendChild(meta);
		body.appendChild(attr);
	}

	/**
	 * Activate one slide / pill pair; deactivate the rest.
	 *
	 * @param {Element[]} slides Slide wrappers.
	 * @param {Element[]} pills Pill buttons.
	 * @param {number} index Active index.
	 * @return {void}
	 */
	function activate(slides, pills, index) {
		var i;
		for (i = 0; i < slides.length; i++) {
			var selected = i === index;
			slides[i].classList.toggle('is-active', selected);
			if (selected) {
				slides[i].removeAttribute('hidden');
				slides[i].setAttribute('aria-hidden', 'false');
			} else {
				slides[i].setAttribute('hidden', '');
				slides[i].setAttribute('aria-hidden', 'true');
			}
			if (pills[i]) {
				pills[i].classList.toggle('is-active', selected);
				if (selected) {
					pills[i].setAttribute('aria-current', 'true');
				} else {
					pills[i].removeAttribute('aria-current');
				}
			}
		}
	}

	/**
	 * Whether this section opts into relocated-link chrome. Keyed off the
	 * section class (the Divi global hook) so new adopters need no page-ID
	 * list in JS or `_voices.scss`.
	 *
	 * @param {Element} section Candidate section.
	 * @return {boolean}
	 */
	function isVoicesChromeScope(section) {
		return section && section.classList.contains('l7-voices-section');
	}

	/**
	 * Move the section-header “Read more stories” link onto the section root so
	 * absolute bottom-right placement isn't trapped by Divi's nested
	 * `position: relative` wrappers (module / column / row). Only runs when
	 * the section carries `.l7-voices-section` (where `.l7-voices__stories-link`
	 * chrome is defined) — elsewhere the link stays in the header.
	 *
	 * @param {Element} section `.l7-voices-section`.
	 * @return {void}
	 */
	function relocateStoriesLink(section) {
		if (!isVoicesChromeScope(section)) {
			return;
		}
		var link = section.querySelector('.l7-section-header__link');
		if (!link || link.getAttribute('data-l7-voices-link') === '1') {
			return;
		}
		link.setAttribute('data-l7-voices-link', '1');
		link.classList.add('l7-voices__stories-link');
		section.appendChild(link);
	}

	/**
	 * Per-carousel interval from `data-l7-interval` (ms). Missing or invalid
	 * values fall back to 6000 so hand-authored homepage `l7_cards` grids keep
	 * today's behavior. Out-of-range numbers are clamped to 2–20s.
	 *
	 * @param {Element} cards `.l7-cards` root.
	 * @return {number}
	 */
	function readIntervalMs(cards) {
		var raw = cards.getAttribute('data-l7-interval');
		if (raw == null || raw === '') {
			return INTERVAL_MS;
		}
		var ms = parseInt(raw, 10);
		if (!isFinite(ms) || ms < 1) {
			return INTERVAL_MS;
		}
		if (ms < INTERVAL_MIN_MS) {
			return INTERVAL_MIN_MS;
		}
		if (ms > INTERVAL_MAX_MS) {
			return INTERVAL_MAX_MS;
		}
		return ms;
	}

	/**
	 * Measure the tallest slide and pin `.l7-voices__track` min-height so
	 * pills / attribution do not jump between quotes of different lengths.
	 *
	 * @param {Element} track `.l7-voices__track`.
	 * @param {Element[]} slides Slide wrappers.
	 * @return {void}
	 */
	function equalizeTrackHeight(track, slides) {
		var max = 0;
		var i;
		var prev = [];

		for (i = 0; i < slides.length; i++) {
			prev[i] = {
				hidden: slides[i].hasAttribute('hidden'),
				active: slides[i].classList.contains('is-active')
			};
			slides[i].classList.add('is-active');
			slides[i].removeAttribute('hidden');
			slides[i].style.setProperty('display', 'block', 'important');
			slides[i].style.visibility = 'hidden';
			slides[i].style.position = 'absolute';
			slides[i].style.left = '0';
			slides[i].style.right = '0';
			slides[i].style.top = '0';
		}

		for (i = 0; i < slides.length; i++) {
			var height = slides[i].offsetHeight;
			if (height > max) {
				max = height;
			}
		}

		for (i = 0; i < slides.length; i++) {
			if (!prev[i].active) {
				slides[i].classList.remove('is-active');
			}
			if (prev[i].hidden) {
				slides[i].setAttribute('hidden', '');
			}
			slides[i].style.removeProperty('display');
			slides[i].style.visibility = '';
			slides[i].style.position = '';
			slides[i].style.left = '';
			slides[i].style.right = '';
			slides[i].style.top = '';
		}

		if (max > 0) {
			track.style.minHeight = max + 'px';
		}
	}

	/**
	 * Debounced window-resize remesure for one carousel.
	 *
	 * @param {Function} fn Measure callback.
	 * @return {Function}
	 */
	function debounceResize(fn) {
		var timer = null;
		return function () {
			if (timer) {
				window.clearTimeout(timer);
			}
			timer = window.setTimeout(fn, 150);
		};
	}

	/**
	 * Enhance a single opted-in voices cards grid into a carousel.
	 *
	 * @param {Element} root `.l7-voices` Divi wrapper (or `.l7-cards`).
	 * @param {number} rootIndex Index among enhanced carousels.
	 * @return {void}
	 */
	function enhance(root, rootIndex) {
		var cards = resolveCardsRoot(root);
		if (cards.getAttribute('data-l7-voices') === '1') {
			return;
		}

		var slides = collectSlides(cards);
		if (slides.length < 2) {
			return;
		}

		var base = uid(rootIndex);
		var i;
		var pills = [];
		var current = 0;
		var timer = null;
		var focusPaused = false;
		var intervalMs = readIntervalMs(cards);

		root.classList.add('l7-voices--enhanced');
		cards.classList.add('l7-voices--enhanced');
		cards.setAttribute('data-l7-voices', '1');
		cards.setAttribute('role', 'region');
		cards.setAttribute('aria-roledescription', 'carousel');
		cards.setAttribute('aria-label', 'Customer voices');

		var live = document.createElement('div');
		live.className = 'l7-voices__live screen-reader-text';
		live.setAttribute('aria-live', 'polite');
		live.setAttribute('aria-atomic', 'true');
		cards.insertBefore(live, cards.firstChild);

		var track = document.createElement('div');
		track.className = 'l7-voices__track';
		cards.insertBefore(track, live.nextSibling);

		for (i = 0; i < slides.length; i++) {
			var slide = slides[i];
			slide.classList.add('l7-voices__slide');
			slide.setAttribute('id', base + '-slide-' + i);
			slide.setAttribute('role', 'group');
			slide.setAttribute('aria-roledescription', 'slide');
			slide.setAttribute(
				'aria-label',
				i + 1 + ' of ' + slides.length
			);
			ensureAvatar(slide);
			track.appendChild(slide);
		}

		var nav = document.createElement('div');
		nav.className = 'l7-voices__pills';
		nav.setAttribute('role', 'group');
		nav.setAttribute('aria-label', 'Slide controls');

		for (i = 0; i < slides.length; i++) {
			var pill = document.createElement('button');
			pill.type = 'button';
			pill.className = 'l7-voices__pill';
			pill.setAttribute(
				'aria-label',
				'Show quote from ' + slideLabel(slides[i], i)
			);
			pill.setAttribute('aria-controls', base + '-slide-' + i);
			nav.appendChild(pill);
			pills.push(pill);
		}

		cards.appendChild(nav);

		/**
		 * @param {number} index Target slide.
		 * @param {boolean} announce Whether to update the live region.
		 * @return {void}
		 */
		function goTo(index, announce) {
			current = ((index % slides.length) + slides.length) % slides.length;
			activate(slides, pills, current);
			if (announce) {
				live.textContent = slideLabel(slides[current], current);
			}
		}

		/**
		 * @return {void}
		 */
		function stopTimer() {
			if (timer) {
				window.clearInterval(timer);
				timer = null;
			}
		}

		/**
		 * @param {boolean} [force] Restart even if slide focus would pause
		 *   (used after an explicit pill click). Reduced motion still wins.
		 * @return {void}
		 */
		function startTimer(force) {
			stopTimer();
			if (!force && focusPaused) {
				return;
			}
			// Reduced motion still auto-advances; `_voices.scss` drops the
			// slide fade so the change is instant rather than animated.
			timer = window.setInterval(function () {
				goTo(current + 1, false);
			}, intervalMs);
		}

		goTo(0, false);
		equalizeTrackHeight(track, slides);
		var remesure = debounceResize(function () {
			equalizeTrackHeight(track, slides);
		});
		window.addEventListener('resize', remesure);
		// Late webfont swap changes per-slide heights; remesure after load
		// and (where supported) after document.fonts.ready.
		window.addEventListener('load', remesure);
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(remesure);
		}

		nav.addEventListener('click', function (event) {
			var target = event.target;
			if (!target || !target.closest) {
				return;
			}
			var button = target.closest('.l7-voices__pill');
			if (!button || !nav.contains(button)) {
				return;
			}
			event.preventDefault();
			goTo(pills.indexOf(button), true);
			// Mouse click leaves focus on the pill; don't let that latch
			// focusPaused and kill autoplay. Always restart the interval.
			focusPaused = false;
			startTimer(true);
		});

		cards.addEventListener('focusin', function (event) {
			var target = event.target;
			// Pill focus is a click/tab on the control, not "reading the
			// quote" — pausing here permanently stops autoplay after a click.
			if (target && target.closest && target.closest('.l7-voices__pill')) {
				return;
			}
			focusPaused = true;
			stopTimer();
		});
		cards.addEventListener('focusout', function (event) {
			if (!cards.contains(event.relatedTarget)) {
				focusPaused = false;
				startTimer();
			}
		});

		if (window.matchMedia) {
			var motionQuery = window.matchMedia(
				'(prefers-reduced-motion: reduce)'
			);
			var onMotionChange = function () {
				startTimer();
			};
			if (motionQuery.addEventListener) {
				motionQuery.addEventListener('change', onMotionChange);
			} else if (motionQuery.addListener) {
				motionQuery.addListener(onMotionChange);
			}
		}

		startTimer();
	}

	/**
	 * @return {void}
	 */
	function init() {
		if (isVisualBuilder()) {
			return;
		}

		var sections = document.querySelectorAll('.l7-voices-section');
		var s;
		for (s = 0; s < sections.length; s++) {
			relocateStoriesLink(sections[s]);
		}

		var roots = document.querySelectorAll(SELECTOR);
		var seen = [];
		var i;

		for (i = 0; i < roots.length; i++) {
			var root = roots[i];
			// Skip sidebar opt-ins if a broader selector ever matches them.
			if (root.classList.contains('l7-voices-sidebar')) {
				continue;
			}
			var cards = resolveCardsRoot(root);
			if (seen.indexOf(cards) !== -1) {
				continue;
			}
			seen.push(cards);
			enhance(root, seen.length - 1);
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})(window, document);
